import React, { useState, useMemo } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, Button, Select, SelectItem, Spinner, Input } from '@heroui/react';
import { Building2, X, AlertCircle, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDepartmentAssignments, useEnrollDepartment, useUnenrollDepartment } from '@/hooks/queries/useCourses';
import { useQuery } from '@tanstack/react-query';
import departmentsApi from '@/services/apis/departments';
import ConfirmationModal from '../../base/ConfirmationModal';

const assignDepartmentSchema = z.object({
  departmentId: z.string().min(1, 'Department is required'),
  deadline: z.string().optional(),
});

type AssignDepartmentFormData = z.infer<typeof assignDepartmentSchema>;

interface AssignDepartmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId?: string | null;
}

const AssignDepartmentsModal: React.FC<AssignDepartmentsModalProps> = ({ isOpen, onClose, courseId }) => {
  const { t } = useTranslation('admin');
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [departmentToRemove, setDepartmentToRemove] = useState<string | null>(null);

  const { control, handleSubmit, reset, watch } = useForm<AssignDepartmentFormData>({
    resolver: zodResolver(assignDepartmentSchema),
    defaultValues: {
      departmentId: '',
      deadline: '',
    },
  });

  const selectedDepartmentId = watch('departmentId');

  const { data: assignments, isLoading: isLoadingAssignments } = useDepartmentAssignments(courseId || '');
  const enrollDepartmentMutation = useEnrollDepartment();
  const unenrollDepartmentMutation = useUnenrollDepartment();

  const { data: departmentsResponse, isLoading: isLoadingDepartments } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await departmentsApi.getDepartments();
      return response.data!;
    },
  });

  const departments = departmentsResponse || [];
  const assignedDepartmentIds = useMemo(() => {
    return new Set(assignments?.map((a) => a.department.id) || []);
  }, [assignments]);

  const availableDepartments = useMemo(() => {
    return departments.filter((dept) => !assignedDepartmentIds.has(dept.id));
  }, [departments, assignedDepartmentIds]);

  const onSubmitEnroll = async (data: AssignDepartmentFormData) => {
    if (!courseId) return;

    await enrollDepartmentMutation.mutateAsync({
      courseId,
      departmentId: data.departmentId,
      data: data.deadline ? { deadline: new Date(data.deadline).toISOString() } : undefined,
    });

    reset({ departmentId: '', deadline: '' });
  };

  const handleUnenrollDepartment = async (departmentId: string) => {
    if (!courseId) return;

    await unenrollDepartmentMutation.mutateAsync({
      courseId,
      departmentId,
    });
    setRemoveConfirmOpen(false);
    setDepartmentToRemove(null);
  };

  const handleRemoveClick = (departmentId: string) => {
    setDepartmentToRemove(departmentId);
    setRemoveConfirmOpen(true);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" placement="center" backdrop="blur" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">{t('courses.assignDepartments.title')}</ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              {/* Info tip */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800 dark:text-blue-200">{t('courses.assignDepartments.infoTip')}</p>
              </div>

              {/* Add department */}
              <form onSubmit={handleSubmit(onSubmitEnroll)} className="space-y-4">
                <h3 className="font-semibold">{t('courses.assignDepartments.addDepartment')}</h3>
                <div className="flex flex-col gap-3">
                  <div className="flex gap-2">
                    <Controller
                      name="departmentId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          placeholder={t('courses.assignDepartments.selectPlaceholder')}
                          selectedKeys={field.value ? [field.value] : []}
                          onSelectionChange={(keys) => {
                            const selected = Array.from(keys)[0] as string;
                            field.onChange(selected || '');
                          }}
                          className="flex-1"
                          isLoading={isLoadingDepartments}
                          isDisabled={availableDepartments.length === 0}
                        >
                          {availableDepartments.map((dept) => (
                            <SelectItem key={dept.id}>{dept.name}</SelectItem>
                          ))}
                        </Select>
                      )}
                    />
                  </div>
                  <div className="flex gap-2 items-end">
                    <Controller
                      name="deadline"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="date"
                          label={t('courses.assignDepartments.deadline')}
                          placeholder={t('courses.assignDepartments.deadlinePlaceholder')}
                          size="sm"
                          className="flex-1"
                          min={new Date().toISOString().split('T')[0]}
                          description={t('courses.assignDepartments.deadlineDescription')}
                          startContent={<Calendar className="w-4 h-4 text-gray-400" />}
                        />
                      )}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                        color="primary"
                        type="submit"
                        isDisabled={!selectedDepartmentId}
                        isLoading={enrollDepartmentMutation.isPending}
                        startContent={<Building2 className="w-4 h-4" />}
                      >
                        {t('courses.assignDepartments.add')}
                      </Button>
                    </div>
                </div>
              </form>

              {/* Currently assigned departments */}
              <div className="space-y-4">
                <h3 className="font-semibold">{t('courses.assignDepartments.assignedDepartments')}</h3>
                {isLoadingAssignments ? (
                  <div className="flex justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : assignments && assignments.length > 0 ? (
                  <div className="space-y-2">
                    {assignments.map((assignment) => (
                      <div
                        key={assignment.id}
                        className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 dark:bg-gray-800"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-500" />
                            <p className="font-medium">{assignment.department.name}</p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {t('courses.assignDepartments.assignedBy')}: {assignment.assignedBy.email} •{' '}
                            {new Date(assignment.assignedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          color="danger"
                          variant="light"
                          isIconOnly
                          onPress={() => handleRemoveClick(assignment.department.id)}
                          isLoading={
                            unenrollDepartmentMutation.isPending && departmentToRemove === assignment.department.id
                          }
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    {t('courses.assignDepartments.noAssignments')}
                  </div>
                )}
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>

      <ConfirmationModal
        isOpen={removeConfirmOpen}
        onClose={() => {
          setRemoveConfirmOpen(false);
          setDepartmentToRemove(null);
        }}
        onConfirm={() => {
          if (departmentToRemove) {
            handleUnenrollDepartment(departmentToRemove);
          }
        }}
        title={t('courses.assignDepartments.removeConfirm.title')}
        message={t('courses.assignDepartments.removeConfirm.message')}
        confirmLabel={t('courses.assignDepartments.removeConfirm.confirm')}
        cancelLabel={t('courses.assignDepartments.removeConfirm.cancel')}
        confirmColor="danger"
        isLoading={unenrollDepartmentMutation.isPending}
      />
    </>
  );
};

export default AssignDepartmentsModal;
